import AuthService from '@apis/AuthService';
import WorkspaceService from '@apis/WorkspaceService';
import { COGNITO_LOGOUT_URL } from '@config/config';
import { MasqueradeUser } from '@customTypes/auth/Auth';
import LocalStorageKey from '@enums/LocalStorageKey';
import { UserRole } from '@enums/Permissions';
import { useEffectOnce } from '@hooks/useEffectOnce';
import * as Sentry from '@sentry/react';
import { setChatContext } from '../libs/sentryChat';
import LocalStorageService from '@services/LocalStorageService';
import SessionStorageService from '@services/SessionStorageService';
import WorkspaceContextService from '@services/WorkspaceContextService';
import { useAppStore } from '@stores/app/appStore';
import { setExecutiveSession } from '@stores/chatbot/executiveSession';
import { clearParkedChatSend } from '@stores/chatbot/parkedChatSend';
import { clearDeferredBeacons } from '@services/chatbot/deliveryOutbox';
import { getCognitoLogoutUrl } from '@utils/federatedAuth';
import { isInternalStaffSession } from '@utils/internalStaff';
import { useAppLoaderStore } from '@stores/appLoaderStore';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { queryClient } from '../libs/queryClient';
import posthog, { aliasLandingSession } from '@libs/posthog';
import { EVENTS } from '@lib/analytics';

const REFRESH_CHANNEL_NAME = 'leonardo_auth_refresh';
// Set when a masquerade begins and consumed on the first post-entry mount.
// Masquerade already does one intentional navigation to '/'; while this
// sentinel is set, loadUserProfile skips the per-tab workspace reload
// machinery (restore / mismatch-reload / smart-landing) so those don't stack
// full-page reloads on top of the entry — each reload re-fans-out ~20 API
// calls, and the stacked bursts tripped the rate limiter (429s).
const MASQUERADE_ENTERED_KEY = 'masquerade_entered';
type RefreshMessage = { type: 'refresh_start' | 'refresh_done'; tabId: string; timestamp: number };
const TAB_ID = `tab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
const PEER_REFRESH_STALE_MS = 15_000;

const authContextKey = (profile: any): string | null => {
  const userId = String(profile?.userId || '').trim();
  const companyName = String(profile?.companyName || '').trim();
  return userId && companyName ? `${userId}:${companyName}` : null;
};

// Fire the registration conversion to the ad pixels (Meta Pixel + GA4). Call
// this exactly once per genuine new signup — never on returning-user logins or
// signup-step navigation, which is what previously inflated the count ~4x.
const fireRegistrationConversion = (method: string, userId?: string) => {
  const w = window as unknown as {
    fbq?: (
      action: string,
      name: string,
      params?: Record<string, unknown>,
      options?: { eventID?: string },
    ) => void;
    gtag?: (...args: unknown[]) => void;
  };
  // Deterministic id shared with the server-side CAPI CompleteRegistration so
  // Meta dedups the browser + server registration into a single event.
  const eventID = userId ? `CompleteRegistration_${userId}` : undefined;
  if (typeof w.fbq === 'function') {
    if (eventID) {
      w.fbq('track', 'CompleteRegistration', {}, { eventID });
    } else {
      w.fbq('track', 'CompleteRegistration');
    }
  }
  if (typeof w.gtag === 'function') {
    w.gtag('event', 'complete_registration', { method });
  }
};

interface SetUserLoginType {
  profile?: any;
}

// Companion to WORKSPACE (which already holds companyName) — captures the bits
// needed to restore a user to their last-active workspace after logout: userId
// (so a different user logging in on the same browser is ignored) and the
// workspaceId needed to call switchWorkspace. workspaceId === null means "home".
type LastAuthContext = {
  userId: string;
  workspaceId: string | null;
};

interface AuthContextType {
  isAuthenticated: boolean;
  accessTokenExpires: number;
  isAdmin: boolean;
  setUserlogin: (data?: SetUserLoginType) => void;
  hasRole: (role: UserRole) => boolean;
  logout: () => void;
  currentUser: any;
  loadUserProfile: (
    masquadeUser?: MasqueradeUser | null,
    isSilent?: boolean,
  ) => void;
  updateOnboardStep: (step: number) => void;
  setMasqueradeLogin: (data: MasqueradeUser | null) => void;
  masqueradeUser: MasqueradeUser | null;
  isSuperAdmin: boolean;
  isMasquerading: boolean;
  isInternalUser: boolean;
  /** True internal STAFF (masquerade, superuser, internal email) — mirrors the
   *  backend's actor_is_internal_staff; never the operator-set is_internal flag. */
  isInternalStaff: boolean;
  isFreeTrialExpired: boolean;
  isBrandingEnabled: boolean;
}

// Create context with explicit null default
export const AuthContext = createContext<AuthContextType | null>(null);
const storage = LocalStorageService;
// Masquerade state lives per-tab: starting an impersonation in one tab
// must not follow the operator's other tabs. Session-scoped storage
// gives us that isolation while attachAuthHeaders reads from it on
// every request.
const tabStorage = SessionStorageService;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [accessTokenExpires, setAccessTokenExpires] = useState<number>(() => {
    try {
      return storage.getItem<number>(LocalStorageKey.ACCESS_TOKEN_EXPIRES) || 0;
    } catch (error) {
      console.warn('Failed to get access token expires from storage:', error);
      return 0;
    }
  });
  const [masqueradeUser, setMasqueradeUser] = useState<MasqueradeUser | null>(
    () => {
      try {
        return tabStorage.getItem<MasqueradeUser>(LocalStorageKey.MASQUERADE_USER) || null;
      } catch (error) {
        return null;
      }
    },
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [isMasquerading, setIsMasquerading] = useState<boolean>(false);
  const [isSilent, setIsSilent] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    try {
      return !!storage.getItem(LocalStorageKey.USER_PROFILE);
    } catch (error) {
      console.warn('Failed to check authentication state:', error);
      return false;
    }
  });
  // Use ref to track authentication state for timeout callbacks
  const isAuthenticatedRef = useRef(isAuthenticated);
  const activeAuthContextRef = useRef<string | null>(null);
  const isRefreshingRef = useRef(false);
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  const setCompanyName = useAppStore((state) => state.setCompanyName);
  const setCompanyDisplayName = useAppStore((state) => state.setCompanyDisplayName);
  const clearAllTabs = useAppStore((state) => state.clearAllTabs);

  const isAdmin = useMemo(() => {
    return (
      currentUser?.user_type !== UserRole.STAFF ||
      currentUser?.user_type === UserRole.ADMIN
    );
  }, [currentUser]);

  const isBrandingEnabled = useMemo(() => {
    return currentUser?.is_branding_enabled;
  }, [currentUser]);

  const isSuperAdmin = useMemo(() => {
    return currentUser?.is_support;
  }, [currentUser]);

  const isFreeTrialExpired = useMemo(() => {
    return currentUser?.is_subscription_expired;
  }, [currentUser]);

  const isInternalUser = useMemo(() => {
    // Persistent per-user flag a staff operator set from Settings → Internal.
    // Once on, this account is internal regardless of its email domain.
    if (currentUser?.is_internal === true) return true;
    const email = currentUser?.user_name ?? '';
    if (email.endsWith('@fibonaccipartner.com') || email.endsWith('@leoads.ai') || email.endsWith('@percuity.ai')) return true;
    const operatorEmail = tabStorage.getItem<string>(LocalStorageKey.MASQUERADE_OPERATOR_EMAIL);
    if (!operatorEmail || !masqueradeUser) return false;
    return operatorEmail.endsWith('@fibonaccipartner.com') || operatorEmail.endsWith('@leoads.ai') || operatorEmail.endsWith('@percuity.ai');
  }, [currentUser, masqueradeUser]);

  const isInternalStaff = useMemo(
    () => isInternalStaffSession(currentUser, Boolean(masqueradeUser) || isMasquerading),
    [currentUser, masqueradeUser, isMasquerading],
  );

  // Tag every event with the session's staff status so analytics can exclude us.
  // Masquerade is the case email filters miss: the session is identified as the
  // customer, but isInternalStaffSession still returns true, so the flag stays on.
  useEffect(() => {
    if (!isAuthenticated) return;
    posthog.register({ is_internal_staff: isInternalStaff });
  }, [isAuthenticated, isInternalStaff]);

  const hasRole = useCallback(
    (role: UserRole) => currentUser?.user_type === role,
    [currentUser],
  );

  const logout = useCallback(async () => {
    try {
      // Preserve workspace-scoped UI state (sidebar tree expand/collapse, panel widths,
      // tab layout, etc.) across logout/login. appStore persist keys are workspace-
      // scoped — one per workspace — and ALL of them survive logout, not just the
      // active one, so switching workspaces after re-login still shows that workspace's
      // saved state. WORKSPACE + LAST_AUTH_CONTEXT survive too so the same user lands
      // back on their last-active workspace; cross-user logins are filtered by userId
      // check in loadUserProfile.
      const appStorePrefix = LocalStorageKey.APP_STORAGE_PREFIX;
      const preservedAppStoreEntries: Record<string, string> = {};
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith(appStorePrefix) && key.endsWith('_app')) {
          const val = localStorage.getItem(key);
          if (val !== null) preservedAppStoreEntries[key] = val;
        }
      }
      const preservedWorkspace = storage.getItem<string>(LocalStorageKey.WORKSPACE);
      const preservedLastAuthContext = storage.getItem<LastAuthContext>(LocalStorageKey.LAST_AUTH_CONTEXT);
      storage.clear();
      sessionStorage.removeItem('smart_landing_done');
      sessionStorage.removeItem(MASQUERADE_ENTERED_KEY);
      // Wipe this tab's per-tab state (workspace pin + masquerade
      // session) so the next user — or the same user logging back in —
      // starts from the cookie default with no impersonation carried
      // over.
      WorkspaceContextService.clear();
      tabStorage.clear();
      // A message parked across a session-expiry reload, and delivery
      // receipts held back by a 401, belong to the person signing out.
      clearParkedChatSend(sessionStorage);
      clearDeferredBeacons(sessionStorage);
      if (preservedWorkspace) {
        storage.setItem(LocalStorageKey.WORKSPACE, preservedWorkspace);
      }
      if (preservedLastAuthContext) {
        storage.setItem(LocalStorageKey.LAST_AUTH_CONTEXT, preservedLastAuthContext);
      }
      for (const [key, val] of Object.entries(preservedAppStoreEntries)) {
        localStorage.setItem(key, val);
      }
      setExecutiveSession(null);
      setIsAuthenticated(false);
      setCurrentUser(null);
      setAccessTokenExpires(0);
      setMasqueradeUser(null);
      activeAuthContextRef.current = null;
      Sentry.setUser(null);
      // End the PostHog identity so a later login (or a masquerade hop) starts a
      // fresh distinct_id instead of inheriting the previous actor's — otherwise
      // identity, and the is_internal_staff super-property, bleed across users.
      posthog.reset();
      queryClient.clear();
      await AuthService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      if (COGNITO_LOGOUT_URL) {
        window.location.href = getCognitoLogoutUrl();
      }
    }
  }, []);

  // Enhanced user profile loading with branding
  const loadUserProfile = useCallback(
    async (
      masquadeUser: MasqueradeUser | null = null,
      isSilent: boolean = false,
    ) => {
    const isMasquerade = typeof masquadeUser !== 'undefined' && masquadeUser;
    setIsSilent(isSilent);
    setLoading(true);
    if (isMasquerade) {
      setIsMasquerading(true);
      // Suppress the reload machinery for this entry and the mount that
      // follows the '/' navigation below (see MASQUERADE_ENTERED_KEY).
      sessionStorage.setItem(MASQUERADE_ENTERED_KEY, 'true');
      // Read operator email from USER_PROFILE (localStorage) to avoid stale closure —
      // currentUser is not in this useCallback's dependency array. Write the operator
      // email to sessionStorage so masquerade stays scoped to THIS tab only.
      // Only capture on the FIRST hop: when switching masquerade→masquerade,
      // USER_PROFILE already holds the previous customer, so preserve the
      // operator email captured when the impersonation first began.
      const userProfile = storage.getItem<any>(LocalStorageKey.USER_PROFILE);
      const operatorEmail = userProfile?.user_name;
      if (operatorEmail && !tabStorage.getItem(LocalStorageKey.MASQUERADE_OPERATOR_EMAIL)) {
        tabStorage.setItem(LocalStorageKey.MASQUERADE_OPERATOR_EMAIL, operatorEmail);
      }
    }

    try {
      const response = await AuthService.getCurrentUser();

      // While a masquerade entry is in flight (the entry call itself and the
      // first mount after its '/' navigation), skip the reload machinery below.
      // Consume the sentinel on that first non-masquerade mount so normal
      // reload behavior resumes for the rest of the masqueraded session.
      const masqueradeEntered =
        sessionStorage.getItem(MASQUERADE_ENTERED_KEY) === 'true';
      if (masqueradeEntered && !isMasquerade) {
        sessionStorage.removeItem(MASQUERADE_ENTERED_KEY);
      }

      // A masquerade start OR a user swap on the same tab (session
      // hijack, silent SSO login, expired session replaced) must drop
      // the previous user's per-tab pin — otherwise every request
      // carries the wrong workspace header for the new identity.
      if (
        isMasquerade
        || (response?.userId && WorkspaceContextService.pinBelongsToDifferentUser(response.userId))
      ) {
        WorkspaceContextService.clear();
      }

      // Both restoration and mismatch detection compare the preserved WORKSPACE
      // (last-active companyName) against the backend's /me response. Read it once.
      const currentWorkspace = storage.getItem<string>(LocalStorageKey.WORKSPACE);
      const incomingWorkspace = response?.companyName;

      // With per-tab workspace isolation, the shared WORKSPACE and
      // LAST_AUTH_CONTEXT keys can be overwritten by another tab's switch.
      // Only tabs that have NOT yet been pinned should consult those keys —
      // a tab with an existing pin has already chosen its workspace and
      // must not be dragged into another tab's choice on reload.
      const tabHasPin = WorkspaceContextService.hasPin();

      // Restore the same user to their last-active workspace after re-login. The
      // backend's /me returns the user's home/default context, not their last
      // workspace — so without this step they'd always land on the wrong workspace
      // after logout. LAST_AUTH_CONTEXT survives logout (see logout()) and is
      // gated by userId so a cross-user login on the same browser is ignored.
      // Runs once per session (RESTORE_KEY) and only when the preserved workspace
      // (WORKSPACE) actually differs from the active one. Skipped entirely for
      // tabs already pinned — they're not "restoring after login," they're just
      // reloading, and their own pin is the source of truth.
      const RESTORE_KEY = 'workspace_restore_attempted';
      const lastAuthContext = storage.getItem<LastAuthContext>(LocalStorageKey.LAST_AUTH_CONTEXT);
      const restoreAttempted = sessionStorage.getItem(RESTORE_KEY) === 'true';
      if (
        !tabHasPin
        && !restoreAttempted
        && !isMasquerade
        && !masqueradeEntered
        && lastAuthContext
        && lastAuthContext.userId === response?.userId
        && currentWorkspace
        && currentWorkspace !== incomingWorkspace
      ) {
        sessionStorage.setItem(RESTORE_KEY, 'true');
        try {
          if (lastAuthContext.workspaceId) {
            await WorkspaceService.switchWorkspace(lastAuthContext.workspaceId);
          } else {
            // Preserved WORKSPACE (localStorage) still holds the home
            // company name — safe to pass through as the pin value.
            await WorkspaceService.clearWorkspace(currentWorkspace ?? '');
          }
          // WORKSPACE already holds the preserved companyName — no need to re-write.
          window.location.reload();
          return;
        } catch (err) {
          // User may have lost access to the workspace — fall through to normal flow.
          console.warn('[AuthContext] Failed to restore last workspace:', err);
          storage.removeItem(LocalStorageKey.LAST_AUTH_CONTEXT);
        }
      }

      // Fresh-login reload for the "different user without logout" case:
      // Zustand stores may have hydrated with the previous user's
      // workspace key. Reload forces rehydration with the correct one.
      // Same tabHasPin guard as above — an already-pinned tab must not
      // reload just because another tab moved the shared WORKSPACE key.

      // Guard against infinite reload loops (e.g., if localStorage write fails)
      const RELOAD_KEY = 'workspace_reload_count';
      const MAX_RELOADS = 3;

      if (!tabHasPin && !masqueradeEntered && currentWorkspace && currentWorkspace !== incomingWorkspace) {
        const reloadCount = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10);

        if (reloadCount >= MAX_RELOADS) {
          console.error('[AuthContext] Max reload attempts reached, aborting reload loop:', {
            previous: currentWorkspace,
            incoming: incomingWorkspace,
            attempts: reloadCount,
          });
          sessionStorage.removeItem(RELOAD_KEY);
          // Continue without reload - user may need to manually refresh or re-login
        } else {
          console.log('[AuthContext] Workspace mismatch detected, reloading:', {
            previous: currentWorkspace,
            incoming: incomingWorkspace,
            attempt: reloadCount + 1,
          });

          sessionStorage.setItem(RELOAD_KEY, String(reloadCount + 1));

          // Update workspace BEFORE reload so stores hydrate with correct key
          storage.setItem(LocalStorageKey.WORKSPACE, incomingWorkspace);
          storage.setItem(LocalStorageKey.USER_PROFILE, response);

          // Reload clears all in-memory state - future-proof for any new stores
          window.location.reload();
          return;
        }
      } else {
        // Clear reload counter on successful load (no mismatch)
        sessionStorage.removeItem(RELOAD_KEY);
      }

      storage.setItem(LocalStorageKey.USER_PROFILE, response);
      storage.setItem(LocalStorageKey.WORKSPACE, response?.companyName);

      // Pin this tab to whatever workspace the backend just resolved for
      // us — but only if this tab hasn't already been pinned (either by
      // a prior seed on this session or by an explicit switch). Once
      // pinned, the tab ignores subsequent cookie changes from other tabs.
      //
      // Always sync companyName from the fresh response — a stale/empty
      // COMPANY_KEY (e.g. from a caller that once wrote setHome('')) is
      // healed here so Zustand persist keys can't collapse to <prefix>__.
      if (response?.companyName) {
        WorkspaceContextService.seedIfUnpinned({
          workspaceId: response.activeWorkspaceId ?? null,
          companyName: response.companyName,
        });
        WorkspaceContextService.syncCompanyName(response.companyName);
      }

      // Silent onboarding and masquerade transitions keep the React tree
      // mounted. Cancel and clear tenant-bound reads before publishing the new
      // auth context. Tenant-scoped query/SSE keys additionally reject late
      // completions from the old workspace.
      const nextAuthContext = authContextKey(response);
      setExecutiveSession(nextAuthContext);
      if (
        activeAuthContextRef.current &&
        nextAuthContext &&
        activeAuthContextRef.current !== nextAuthContext
      ) {
        await queryClient.cancelQueries();
        queryClient.clear();
      }
      activeAuthContextRef.current = nextAuthContext;

      let masquadedUser = tabStorage.getItem(
        LocalStorageKey.MASQUERADE_USER,
      ) as MasqueradeUser | null;

      if (isMasquerade) {
        masquadedUser = masquadeUser;
      }

      Sentry.setUser({
        id: response?.userId,
        username: response?.name,
      });
      setChatContext({ workspaceName: response?.companyName });

      if ('company_active' in response && !response?.company_active) {
        logout();
      }

      setCurrentUser(response);
      setIsAuthenticated(true);

      if (response?.userId && !isMasquerade) {
        posthog.identify(response.userId, {
          email: response.user_name,
          company_name: response.companyDisplayName,
          subscription_status: response.subscription?.status ?? null,
        }, {
          leo_account_timestamp: response.account_timestamp ?? response.timestamp ?? new Date().toISOString(),
        });
        aliasLandingSession();
        posthog.reloadFeatureFlags();
      }

      // Set masquerade user session (per-tab: sessionStorage)
      tabStorage.setItem(LocalStorageKey.MASQUERADE_USER, masquadedUser);
      setMasqueradeUser(masquadedUser);

      // Set company name after user is authenticated
      if (response?.companyName) {
        setCompanyName(response.companyName);
      }
      if (response?.companyDisplayName) {
        setCompanyDisplayName(response.companyDisplayName);
      }

      if (isMasquerade) {
        // Clear React Query cache when masquerading to prevent showing previous user's data
        queryClient.clear();
        // Clear all browser-style tabs when masquerading
        clearAllTabs();
        window.location.href = '/';
      }

      // Smart login landing: staff with exactly one workspace auto-switch
      // into it on login. Backend flags this via defaultWorkspaceId on /me.
      // Only fires once per session (tracked in sessionStorage) and never
      // when the user is already in a workspace context.
      const defaultWsId = (response as any)?.defaultWorkspaceId;
      const alreadyInWorkspace = Boolean((response as any)?.homeCompany);
      const smartLandingDone =
        sessionStorage.getItem('smart_landing_done') === 'true';
      if (
        defaultWsId &&
        !alreadyInWorkspace &&
        !isMasquerade &&
        !masqueradeEntered &&
        !smartLandingDone
      ) {
        sessionStorage.setItem('smart_landing_done', 'true');
        try {
          const { workspace } = await WorkspaceService.switchWorkspace(
            defaultWsId,
          );
          storage.setItem(
            LocalStorageKey.WORKSPACE,
            workspace?.companyName || '',
          );
          window.location.reload();
          return;
        } catch (err) {
          console.warn(
            '[AuthContext] Smart login landing failed, staying at parent:',
            err,
          );
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      logout();
    } finally {
      setLoading(false);
      setIsMasquerading(false);
    }
  }, [logout, setCompanyName, setCompanyDisplayName, clearAllTabs]);

  const setMasqueradeLogin = useCallback((user: MasqueradeUser | null) => {
    loadUserProfile(user);
  }, [loadUserProfile]);

  const updateOnboardStep = useCallback(
    (step: number) => {
      if (currentUser) {
        const updatedUser = {
          ...currentUser,
          onboard_completed_step: step,
        };
        setCurrentUser(updatedUser);
        storage.setItem(LocalStorageKey.USER_PROFILE, updatedUser);
      }
    },
    [currentUser],
  );

  const setUserlogin = useCallback(
    ({ profile }: SetUserLoginType = {}) => {
      try {
        if (profile) {
          const nextAuthContext = authContextKey(profile);
          setExecutiveSession(nextAuthContext);
          if (
            activeAuthContextRef.current &&
            nextAuthContext &&
            activeAuthContextRef.current !== nextAuthContext
          ) {
            void queryClient.cancelQueries();
            queryClient.clear();
          }
          activeAuthContextRef.current = nextAuthContext;
          setIsAuthenticated(true);
          setMasqueradeUser(null);
          Sentry.setUser({
            id: profile?.userId,
            username: profile?.name,
          });
          storage.setItem(LocalStorageKey.USER_PROFILE, profile);
          setCurrentUser(profile);
        } else {
          loadUserProfile();
        }
      } catch (error) {
        console.error('Error during login:', error);
      }
    },
    [loadUserProfile],
  );


  // Load user profile on initial mount if authenticated or returning from federated login
  useEffectOnce(() => {
    if (isAuthenticated) {
      !currentUser && loadUserProfile();
      return;
    }
    setLoading(false);
  }, [isAuthenticated]);

  // Guards the federated-signup conversion so it fires once per callback even if
  // this effect re-runs (StrictMode double-invoke, isAuthenticated changes).
  const federatedConversionFiredRef = useRef(false);

  // Handle federated login/signup callback
  useEffect(() => {
    // Backend redirects with federated_login_success=1 (home) or federated_signup_success=1 (signup);
    // we have no USER_PROFILE in localStorage yet, so load profile from cookie once.
    const params = new URLSearchParams(window.location.search);
    const isFederatedLogin = params.get('federated_login_success') === '1';
    const isFederatedSignup = params.get('federated_signup_success') === '1';
    if (isFederatedLogin || isFederatedSignup) {
      // Load profile early so PostHog identify() + cross-domain alias run
      // before the user interacts with any forms.
      loadUserProfile()
        .then(() => {
          // Federated signup skips the email/password + verification steps.
          // Backfill those events (after identify) so the ordered funnel stays intact.
          if (isFederatedSignup) {
            posthog.capture(EVENTS.SIGNUP.ACCOUNT_CREATED, { method: 'federated' });
            posthog.capture(EVENTS.SIGNUP.EMAIL_VERIFIED, { method: 'federated' });
            // Fire the registration conversion exactly once, after the account
            // is confirmed created. method reflects the provider the user chose.
            if (!federatedConversionFiredRef.current) {
              federatedConversionFiredRef.current = true;
              const provider =
                storage.getItem<string>(LocalStorageKey.FEDERATED_SIGNUP_PROVIDER) ||
                'federated';
              const newUserId = storage.getItem<{ userId?: string }>(
                LocalStorageKey.USER_PROFILE,
              )?.userId;
              fireRegistrationConversion(provider, newUserId);
              storage.removeItem(LocalStorageKey.FEDERATED_SIGNUP_PROVIDER);
            }
          }
        })
        .finally(() => {
          window.history.replaceState({}, '', window.location.pathname);
          setLoading(false);
        });
    }
  }, [isAuthenticated])

  // Check the access token expires from local storage
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const checkStorage = setInterval(() => {
      try {
        const expiresAt = storage.getItem<number>(LocalStorageKey.ACCESS_TOKEN_EXPIRES);
        if (expiresAt && expiresAt !== accessTokenExpires) {
          setAccessTokenExpires(expiresAt);
        }
      } catch (error) {
        console.warn('Failed to get access token expires from storage:', error);
      }
    }, 1000);
    return () => clearInterval(checkStorage);
  }, [isAuthenticated, accessTokenExpires]);

  // Coordinate refresh across tabs via BroadcastChannel to prevent refresh storms.
  // When one tab starts a refresh, others wait for the result instead of duplicating.
  const refreshChannelRef = useRef<BroadcastChannel | null>(null);
  const peerRefreshingRef = useRef(false);
  const peerRefreshStartedAtRef = useRef(0);

  useEffect(() => {
    try {
      const channel = new BroadcastChannel(REFRESH_CHANNEL_NAME);
      refreshChannelRef.current = channel;
      channel.onmessage = (event: MessageEvent<RefreshMessage>) => {
        if (event.data.tabId === TAB_ID) return;
        if (event.data.type === 'refresh_start') {
          peerRefreshingRef.current = true;
          peerRefreshStartedAtRef.current = event.data.timestamp || Date.now();
        } else if (event.data.type === 'refresh_done') {
          peerRefreshingRef.current = false;
          peerRefreshStartedAtRef.current = 0;
        }
      };
      return () => { channel.close(); refreshChannelRef.current = null; };
    } catch {
      // BroadcastChannel not supported (e.g. some WebViews) - no-op
    }
  }, []);

  const coordinatedRefresh = useCallback(async () => {
    if (
      peerRefreshingRef.current &&
      Date.now() - peerRefreshStartedAtRef.current > PEER_REFRESH_STALE_MS
    ) {
      peerRefreshingRef.current = false;
      peerRefreshStartedAtRef.current = 0;
    }

    if (!isAuthenticatedRef.current || isRefreshingRef.current || peerRefreshingRef.current) return;
    isRefreshingRef.current = true;
    try {
      refreshChannelRef.current?.postMessage({ type: 'refresh_start', tabId: TAB_ID, timestamp: Date.now() } satisfies RefreshMessage);
      await AuthService.refreshToken();
    } catch {
      // Refresh failure is handled by the ApiService interceptor
    } finally {
      isRefreshingRef.current = false;
      refreshChannelRef.current?.postMessage({ type: 'refresh_done', tabId: TAB_ID, timestamp: Date.now() } satisfies RefreshMessage);
    }
  }, []);

  // Set up a timeout for refreshing the access token
  useEffect(() => {
    if (!accessTokenExpires || !isAuthenticated) {
      return;
    }

    const timeToExpiration = (accessTokenExpires - Math.floor(Date.now() / 1000)) - 30;
    if (timeToExpiration <= 0) {
      coordinatedRefresh();
      return;
    }

    const timeout = setTimeout(coordinatedRefresh, timeToExpiration * 1000);
    return () => clearTimeout(timeout);
  }, [accessTokenExpires, isAuthenticated, coordinatedRefresh]);

  // Refresh token when returning to a backgrounded tab.
  // Chrome aggressively throttles setTimeout in background tabs, so the
  // proactive timer above may not fire while the tab is hidden. This
  // listener catches the case where the user returns after the access
  // token cookie has already expired.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticatedRef.current && !isRefreshingRef.current) {
        const expiresAt = storage.getItem<number>(LocalStorageKey.ACCESS_TOKEN_EXPIRES) || 0;
        const now = Math.floor(Date.now() / 1000);
        if (expiresAt - now < 60) {
          coordinatedRefresh();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [coordinatedRefresh]);

  // Loading check for authentication
  const isAppLoading = useMemo(() => {
    return loading && !isSilent;
  }, [loading, isSilent]);

  // Drive the shared boot-loader flag from the auth lifecycle. The 'auth' flag
  // is raised in the store's initial state (see appLoaderStore), so the very
  // first render of AppLoaderGate paints the loader before this effect fires.
  // We lower it here once auth resolves, and re-raise if a later refresh flips
  // us back into loading — so a token-refresh mid-session shows the loader
  // again without needing another mount point.
  useEffect(() => {
    const { raise, lower } = useAppLoaderStore.getState();
    if (isAppLoading) {
      raise('auth');
    } else {
      lower('auth');
    }
  }, [isAppLoading]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      accessTokenExpires,
      isAdmin,
      setUserlogin,
      logout,
      currentUser,
      loadUserProfile,
      updateOnboardStep,
      hasRole,
      setMasqueradeLogin,
      masqueradeUser,
      isSuperAdmin,
      isMasquerading,
      isInternalUser,
      isInternalStaff,
      isFreeTrialExpired,
      isBrandingEnabled,
    }),
    [
      isAuthenticated,
      accessTokenExpires,
      isAdmin,
      setUserlogin,
      logout,
      currentUser,
      loadUserProfile,
      updateOnboardStep,
      hasRole,
      setMasqueradeLogin,
      masqueradeUser,
      isSuperAdmin,
      isMasquerading,
      isInternalUser,
      isInternalStaff,
      isFreeTrialExpired,
      isBrandingEnabled,
    ],
  );

  // Always render AuthContext.Provider so useAuth never sees a missing provider when
  // children are mounted. Showing the app loader by omitting the provider caused
  // races (e.g. loadUserProfile() during onboarding) where descendants still ran useAuth.
  //
  // The visual loader is painted by AppLoaderGate (top of tree) whenever the
  // 'auth' flag is raised in appLoaderStore; here we still structurally gate
  // children so consumers of useAuth() don't render against a half-populated
  // context. Rendering ``null`` while loading is equivalent to the old loader
  // branch from the descendants' perspective — the visible loader is on top
  // via AppLoaderGate.
  return (
    <AuthContext.Provider value={value}>
      {isAppLoading ? null : children}
    </AuthContext.Provider>
  );
};

// Enhanced useAuth hook with better error handling
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === null) {
    // More detailed error message
    const error = new Error(
      'useAuth must be used within an AuthProvider. ' +
      'Make sure your component is wrapped in <AuthProvider>. ' +
      'Check your component hierarchy and ensure AuthProvider is at the root level.',
    );

    // Add debugging info
    console.error('Auth Context Error Details:', {
      component: 'useAuth',
      error: 'Context is null',
      possibleCauses: [
        'Component is outside AuthProvider',
        'AuthProvider not properly imported',
        'Multiple React instances',
        'Component rendered during SSR without proper setup',
      ],
      stackTrace: new Error().stack,
    });

    throw error;
  }

  return context;
};

// Safe hook that doesn't throw errors (useful for error boundaries)
export const useSafeAuth = () => {
  try {
    const context = useContext(AuthContext);
    return context;
  } catch (error) {
    console.warn('useSafeAuth: Auth context not available:', error);
    return null;
  }
};

// Hook to check if auth is available
export const useAuthAvailable = () => {
  try {
    const context = useContext(AuthContext);
    return context !== null;
  } catch (error) {
    return false;
  }
};
